import { useAuth } from "@/context/AuthContext";
import { Navigate, useLocation, Link, useNavigate } from "react-router-dom";
import {
  ChartBar, ClipboardText, Files, Users, Wrench, Package,
  Warning, Trash, FileArrowUp, SignOut, ChartLineUp, CalendarBlank, Gauge, User, Flask, Buildings, PaintBrush, CloudArrowUp, TrafficSign, AddressBook, Gavel, FileText, ArrowsMerge, Calculator, DotsSixVertical, Barricade, Broom } from "@phosphor-icons/react";
import { EmpresaSelector } from "@/components/EmpresaSelector";
import { ROLES_SEM_FINANCEIRO } from "@/components/pcp";

const NAV = [
  { to: "/", label: "Dashboard", icon: Gauge, testid: "nav-dashboard" },
  { group: "Comercial" },
  { to: "/clientes", label: "Clientes", icon: AddressBook, testid: "nav-clientes" },
  { to: "/licitacoes", label: "Licitações / Oportunidades", icon: Gavel, testid: "nav-licitacoes" },
  { to: "/orcamentos", label: "Orçamentos", icon: Calculator, testid: "nav-orcamentos", financeiro: true },
  { to: "/propostas", label: "Propostas", icon: FileText, testid: "nav-propostas", financeiro: true },
  { group: "Operacional" },
  { to: "/contratos", label: "Contratos", icon: Files, testid: "nav-contratos" },
  { to: "/ordens", label: "Ordens de Serviço", icon: ClipboardText, testid: "nav-ordens" },
  { to: "/programacao", label: "Agenda Geral do Grupo", icon: CalendarBlank, testid: "nav-programacao" },
  { to: "/apontamento", label: "Apontamento Avulso", icon: Wrench, testid: "nav-apontamento", admin: true },
  { to: "/apontamento-pintura", label: "Apontar Pintura", icon: PaintBrush, testid: "nav-apontamento-pintura" },
  { to: "/apontamento-placas", label: "Apontar Placas", icon: TrafficSign, testid: "nav-apontamento-placas" },
  { to: "/apontamento-tachas", label: "Apontar Tachas", icon: DotsSixVertical, testid: "nav-apontamento-tachas" },
  { to: "/apontamento-defensas", label: "Apontar Defensas", icon: Barricade, testid: "nav-apontamento-defensas" },
  { to: "/apontamento-capina", label: "Apontar Capina/Limpeza", icon: Broom, testid: "nav-apontamento-capina" },
  { to: "/apontamentos", label: "Produção / Histórico", icon: ChartBar, testid: "nav-apontamentos-list" },
  { to: "/fila-offline", label: "Fila Offline", icon: CloudArrowUp, testid: "nav-fila-offline" },
  { group: "Cadastros compartilhados" },
  { to: "/empresas", label: "Empresas e Consórcios", icon: Buildings, testid: "nav-empresas" },
  { to: "/equipes", label: "Equipes", icon: Users, testid: "nav-equipes" },
  { to: "/composicoes", label: "Composições", icon: Flask, testid: "nav-composicoes" },
  { to: "/insumos", label: "Almoxarifado", icon: Package, testid: "nav-insumos" },
  { group: "Qualidade" },
  { to: "/nao-conformidades", label: "Não Conformidades", icon: Warning, testid: "nav-ncs" },
  { to: "/relatorios", label: "Relatórios", icon: ChartLineUp, testid: "nav-relatorios" },
  { group: "Administração" },
  { to: "/usuarios", label: "Usuários", icon: User, testid: "nav-usuarios" },
  { to: "/migracao-multiempresa", label: "Migração Multiempresa", icon: ArrowsMerge, testid: "nav-migracao", admin: true },
  { to: "/importacao", label: "Importação Excel", icon: FileArrowUp, testid: "nav-importacao" },
  { to: "/lixeira", label: "Lixeira", icon: Trash, testid: "nav-lixeira" },
];

export const escopoContrato = (user) => user?.role === "gestor" && (user?.contratos_ids || []).length > 0;
export const veDashboard = (user) => ["admin", "gestor"].includes(user?.role) && !escopoContrato(user);
const GESTOR_CONTRATO_NAV = ["/contratos", "/ordens", "/programacao", "/apontamento-pintura", "/apontamento-placas", "/apontamento-tachas", "/apontamento-defensas", "/apontamento-capina", "/apontamentos", "/fila-offline", "/equipes", "/composicoes", "/nao-conformidades"];
const GESTOR_CONTRATO_GRUPOS = ["Operacional", "Cadastros compartilhados", "Qualidade"];

function Sidebar() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  return (
    <aside className="sidebar" data-testid="sidebar">
      <div className="sidebar-logo">
        <div className="flex items-center gap-3">
          <div style={{ width: 36, height: 36, background: "#FFCC00", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 3 }}>
            <span className="font-display font-black text-black text-lg">PCP</span>
          </div>
          <div>
            <div className="font-display font-black text-white leading-none" style={{ fontSize: "0.95rem" }}>SINALIZAÇÃO</div>
            <div className="overline" style={{ color: "#FFCC00" }}>VIÁRIA · v1.0</div>
          </div>
        </div>
      </div>
      <nav className="sidebar-nav">
        <EmpresaSelector />
        {NAV.filter((n) => (user?.role === "fiscal" ? ["/contratos", "/ordens"].includes(n.to) : escopoContrato(user) ? (n.group ? GESTOR_CONTRATO_GRUPOS.includes(n.group) : GESTOR_CONTRATO_NAV.includes(n.to)) : (n.to !== "/" || veDashboard(user)) && (!n.admin || user?.role === "admin") && (!n.financeiro || !ROLES_SEM_FINANCEIRO.includes(user?.role)))).map((n, i) =>
          n.group ? (
            <div key={i} className="sidebar-group">{n.group}</div>
          ) : (
            <Link
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              className={`sidebar-item ${(loc.pathname + loc.search) === n.to || (loc.pathname === n.to && !n.to.includes("?")) ? "active" : ""}`}
            >
              <n.icon size={18} weight="bold" />
              <span>{n.label}</span>
            </Link>
          )
        )}
      </nav>
      <div className="sidebar-footer">
        <div className="flex items-center gap-2 mb-3">
          <div style={{ width: 32, height: 32, borderRadius: 3, background: "#27272A", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <User size={16} weight="bold" color="#FFCC00" />
          </div>
          <div style={{ minWidth: 0 }}>
            <div className="text-white text-sm font-semibold truncate">{user?.name}</div>
            <div className="text-xs" style={{ color: "#71717A", textTransform: "uppercase", letterSpacing: "0.1em" }}>{user?.role}</div>
          </div>
        </div>
        <button
          className="btn btn-ghost btn-sm btn-block"
          data-testid="btn-logout"
          onClick={logout}
          style={{ color: "#FAFAFA", borderColor: "#27272A", justifyContent: "flex-start" }}
        >
          <SignOut size={16} weight="bold" /> Sair
        </button>
      </div>
    </aside>
  );
}

function MobileNav() {
  const loc = useLocation();
  const { user } = useAuth();
  const items = [
    { to: "/", label: "Painel", icon: Gauge },
    { to: "/ordens", label: "OS", icon: ClipboardText },
    { to: "/apontamento-pintura", label: "Pintura", icon: PaintBrush },
    { to: "/apontamento-placas", label: "Placas", icon: TrafficSign },
    { to: "/apontamento-tachas", label: "Tachas", icon: DotsSixVertical },
    { to: "/apontamento-defensas", label: "Defensas", icon: Barricade },
    { to: "/apontamento-capina", label: "Capina", icon: Broom },
    ...(user?.role === "admin" ? [{ to: "/apontamento", label: "Avulso", icon: Wrench }] : []),
    { to: "/fila-offline", label: "Offline", icon: CloudArrowUp },
  ].filter((i) => (user?.role !== "fiscal" || ["/contratos", "/ordens"].includes(i.to)) && (i.to !== "/" || veDashboard(user)));
  return (
    <nav className="mobile-bottom-nav" data-testid="mobile-nav">
      {items.map((n) => (
        <Link key={n.to} to={n.to} className={`mobile-bottom-item ${loc.pathname === n.to ? "active" : ""}`}>
          <n.icon size={20} weight="bold" />
          <span>{n.label}</span>
        </Link>
      ))}
    </nav>
  );
}

export default function AppLayout({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ padding: 32 }}>Carregando…</div>;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="app-shell">
      <Sidebar />
      <div className="main-content">
        <div className="safety-stripes" />
        {user.role === "fiscal" && (
          <div style={{ background: "#FFFBEA", borderBottom: "1px solid #FFCC00", padding: "6px 16px", fontSize: "0.78rem", color: "#78350F" }} data-testid="banner-fiscal">
            Modo fiscalização · somente consulta dos contratos sob sua responsabilidade ({(user.contratos_ids || []).length}). Ações de edição não estão disponíveis.
          </div>
        )}
        {escopoContrato(user) && (
          <div style={{ background: "#F0F9FF", borderBottom: "1px solid #BAE6FD", padding: "6px 16px", fontSize: "0.78rem", color: "#0C4A6E" }} data-testid="banner-gestor-contrato">
            Gestor de contrato · você opera apenas os {(user.contratos_ids || []).length} contrato(s) sob sua responsabilidade.
          </div>
        )}
        {children}
      </div>
      <MobileNav />
    </div>
  );
}
